<template>
  <base-print
    title="WARRANTY RECEIPT"
    :document-number="invoice.invoice_code"
    :document-date="invoice.invoice_date"
    document-label="Invoice #"
    print-id="deliveryReceiptPrint"
  >
    <!-- Customer Information -->
    <div class="info-section">
      <div class="info-row">
        <span class="info-label">Name:</span>
        {{ invoice.customer.customer_name }}
      </div>
      <div class="info-row">
        <span class="info-label">Contact #:</span>
        {{ invoice.customer.customer_contact_no }}
      </div>
      <div class="info-row">
        <span class="info-label">Address:</span>
        Purok {{ invoice.customer.customer_purok }}, Brgy.
        {{ invoice.customer.customer_barangay }},
        {{ invoice.customer.customer_municipality }}
      </div>
    </div>

    <div style="border-top: 2px dashed #000; margin: 15px 0"></div>

    <!-- Items Table -->
    <table class="data-table">
      <thead>
        <tr>
          <th style="width: 60px">ID</th>
          <th>Product Description</th>
          <th style="width: 150px">Batch Code</th>
          <th v-if="printType === 'mixed'" style="width: 100px">Warranty</th>
          <th v-else style="width: 80px" class="text-center">Quantity</th>
        </tr>
      </thead>
      <tbody>
        <tr v-for="item in filteredItems" :key="item.id">
          <td>{{ item.item.id }}</td>
          <td>{{ item.item.item_name }}</td>
          <td>
            {{
              item.item.import_batch
                ? item.item.import_batch.batch_description
                : "N/A"
            }}
          </td>
          <td v-if="printType === 'mixed'">
            {{
              item.item.warranty
                ? item.item.warranty.warranty_count +
                  "-" +
                  item.item.warranty.warranty_duration
                : "N/A"
            }}
          </td>
          <td v-else class="text-center">{{ item.quantity }}</td>
        </tr>
      </tbody>
    </table>

    <!-- Remarks -->
    <div v-if="remarks" style="margin: 15px 0">
      <p style="margin: 0; font-size: 10pt">
        <strong>Remarks:</strong> {{ remarks }}
      </p>
    </div>

    <div style="border-top: 2px dashed #000; margin: 15px 0"></div>

    <!-- Warranty Terms -->
    <div style="margin-top: 20px">
      <h3 style="font-size: 12pt; font-weight: bold; margin: 10px 0">
        JMB COMPUTERS Warranty
      </h3>

      <p style="font-size: 9pt; line-height: 1.3; margin: 10px 0">
        JMB COMPUTERS warranties its products contained in their original
        packaging against material and workmanship defects when the products are
        used normally for their intended purposes for a period of ONE (1) YEAR
        (except if it is stated above in its corresponding row) from the date of
        purchase by the original end-user ("Warranty Period").
      </p>

      <ol style="font-size: 9pt; margin-left: 15px; line-height: 1.3">
        <li style="margin-bottom: 8px">
          <strong>A. Seven-day Outright Replacement</strong> - We allow all
          merchandise in new condition, with original packaging and original
          receipt, to be returned or exchanged within seven (7) days of purchase
          (Except if stated above). To return or exchange items, you must bring
          the item(s) in its original packaging (NO PACKAGING NO RETURN), Proof
          of purchase (Sales Receipt). Exclusions and Limitations apply.
        </li>

        <li style="margin-bottom: 8px">
          <strong>B. Standard Warranty</strong> - After seven days, merchandise
          is no longer eligible for outright replacement but can still be sent
          in for warranty claims as long as it is within the Standard Warranty
          Period or as stated above. For warranty claims, bring the following to
          any JMBComputers Store:
          <ul style="margin-left: 15px; margin-top: 3px">
            <li>Item with JMBComputers Warranty Sticker</li>
            <li>
              Proof of Purchase - Sales Receipt. JMBComputers reserves the right
              to determine whether the merchandise is defective and to replace
              merchandise if necessary.
            </li>
          </ul>
          <p style="margin: 5px 0 0 0">
            Depending on stock availability, it may take up to 30 days or more
            to fulfill a warranty claim. If the item is end-of-life, in-store
            credits will be given to the customer instead, which can be used to
            purchase items of equivalent value. Customers may opt to add cash to
            this store credit to purchase items of higher value. JMBComputers
            reserves the right to decline any of the returns or exchanges when
            deemed necessary.
          </p>
        </li>

        <li style="margin-bottom: 8px">
          <strong>C. Conditions for Warranty</strong>
          <ul style="margin-left: 15px; margin-top: 3px">
            <li>No physical damage should be present on the unit.</li>
            <li>
              Liquid damage, impact damage, and major scratches will void the
              warranty.
            </li>
            <li>
              Burnt components inside or outside the unit will void the
              warranty.
            </li>
            <li>
              Present the unit together with the receipt, boxes, and other
              components included in the package.
            </li>
          </ul>
        </li>
      </ol>
    </div>

    <!-- Signature Section -->
    <div style="margin-top: 30px">
      <p style="font-size: 10pt; margin: 10px 0">
        I have read, understood, and agree to the warranty terms and conditions
        stated above.
      </p>
      <div style="margin-top: 25px">
        <p style="margin: 5px 0; font-size: 10pt">
          Signed for Acceptance and Approval:
        </p>
        <div
          style="
            border-top: 1px solid #000;
            width: 350px;
            padding-top: 5px;
            margin-top: 25px;
            text-align: center;
          "
        >
          Customer Signature
        </div>
        <p style="font-size: 9pt; margin-top: 5px">
          Date: {{ formatDate(new Date()) }}
        </p>
      </div>
    </div>
  </base-print>
</template>

<script>
import BasePrint from "./BasePrint.vue";
import moment from "moment";

export default {
  name: "PrintDeliveryReceipt",
  components: {
    BasePrint,
  },
  props: {
    invoice: {
      type: Object,
      required: true,
    },
    printType: {
      type: String,
      default: "mixed", // 'mixed' or 'quantity'
    },
    remarks: {
      type: String,
      default: "",
    },
  },
  computed: {
    filteredItems() {
      // Filter out tax fee items (id 650)
      return this.invoice.invoice_items.filter((item) => item.item.id !== 650);
    },
  },
  methods: {
    formatDate(date) {
      return moment(date).format("MMMM DD, YYYY");
    },
  },
};
</script>
